<template>
  <div class="order-products">
    <div class="order-products__header">
      <h3 class="order-products__title">Товары</h3>
      <p class="order-products__total">
        Сумма: <strong>{{ productsTotalLabel }}</strong>
      </p>
    </div>

    <div class="order-products__table-head">
      <span class="order-products__head-cell order-products__head-cell--id"
        >ID товара</span
      >
      <span
        class="order-products__head-cell order-products__head-cell--thumb"
        title="Фото"
        >Фото</span
      >
      <span class="order-products__head-cell order-products__head-cell--product"
        >Товар *</span
      >
      <span class="order-products__head-cell order-products__head-cell--qty"
        >Кол-во *</span
      >
      <span class="order-products__head-cell order-products__head-cell--unit"
        >Цена за ед. (индивид. заказ)</span
      >
      <span
        class="order-products__head-cell order-products__head-cell--subtotal"
        >Сумма</span
      >
      <span
        class="order-products__head-cell order-products__head-cell--actions"
      />
    </div>

    <div
      v-for="(line, idx) in lines"
      :key="line._lineId || `p-${idx}`"
      class="order-products__row"
    >
      <div class="order-products__col order-products__col--id">
        <p
          class="order-products__product-id"
          :title="
            line.product_id != null && line.product_id !== ''
              ? 'ID позиции в каталоге'
              : ''
          "
        >
          {{
            line.product_id != null && line.product_id !== ""
              ? `#${line.product_id}`
              : "—"
          }}
        </p>
      </div>
      <div class="order-products__col order-products__col--thumb">
        <div class="order-products__thumb-wrap">
          <img
            v-if="line.product_thumb_url"
            :src="line.product_thumb_url"
            alt=""
            class="order-products__thumb"
            loading="lazy"
          />
          <span v-else class="order-products__thumb-placeholder" aria-hidden="true"
            >—</span
          >
        </div>
      </div>
      <div class="order-products__col order-products__col--product">
        <div class="order-products__product-row">
          <input
            :value="line.productSearch"
            class="order-products__input order-products__input--product"
            type="text"
            autocomplete="off"
            placeholder="Название (поиск по каталогу)"
            aria-label="Товар"
            @input="onProductLineSearchInput(idx, $event.target.value)"
          />
          <router-link
            v-if="line.product_slug"
            :to="{
              name: 'product-edit',
              params: { slug: line.product_slug },
            }"
            class="order-products__product-link"
            target="_blank"
            rel="noopener noreferrer"
            title="Карточка товара в админке (новая вкладка)"
            @click.native.stop
          >
            <va-icon name="fa fa-external-link" />
          </router-link>
          <span
            v-else
            class="order-products__product-link order-products__product-link--inactive"
            title="Ссылка появится после выбора товара"
            aria-hidden="true"
          >
            <va-icon name="fa fa-external-link" />
          </span>
        </div>
        <div
          v-if="line.productSearchResults && line.productSearchResults.length"
          class="order-products__search-results"
        >
          <button
            v-for="p in line.productSearchResults"
            :key="p.id"
            type="button"
            class="order-products__search-item"
            @click="selectProductForLine(idx, p)"
          >
            <span class="order-products__search-name">{{ p.name }}</span>
            <span class="order-products__search-id">#{{ p.id }}</span>
          </button>
        </div>
      </div>

      <div class="order-products__col order-products__col--qty">
        <input
          :value="line.quantity"
          class="order-products__input"
          type="number"
          min="1"
          required
          aria-label="Количество"
          @input="
            updateLine(idx, {
              quantity: parseNumberOrEmpty($event.target.value),
            })
          "
        />
      </div>

      <div class="order-products__col order-products__col--unit">
        <input
          :value="line.unit_price"
          class="order-products__input"
          type="number"
          min="0"
          step="0.01"
          aria-label="Цена за единицу"
          @input="
            updateLine(idx, {
              unit_price: parseNumberOrEmpty($event.target.value),
            })
          "
        />
      </div>

      <div class="order-products__col order-products__col--subtotal">
        <p class="order-products__line-subtotal">
          {{ lineSubtotalLabel(line) }}
        </p>
      </div>

      <div class="order-products__col order-products__col--actions">
        <button
          :disabled="lines.length === 1"
          type="button"
          class="order-products__remove-btn"
          @click="removeProductLine(idx)"
        >
          X
        </button>
      </div>
    </div>

    <button
      type="button"
      class="order-products__add-btn"
      @click="addProductLine"
    >
      + Товар
    </button>
  </div>
</template>

<script>
import api from "@/api/axios";

export default {
  name: "OrderProductsEditor",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      nextProductLineId: 1,
      productLineSearchTimers: {},
      lines: [],
      syncingFromValue: false,
    };
  },
  computed: {
    productsTotalAmount() {
      let sum = 0;
      let has = false;
      for (const line of this.lines) {
        if (line.product_id == null || line.product_id === "") continue;
        const sub = this.lineSubtotalAmount(line);
        if (sub == null) continue;
        sum += sub;
        has = true;
      }
      return has ? sum : null;
    },
    productsTotalLabel() {
      const n = this.productsTotalAmount;
      if (n == null) return "—";
      return this.formatMoneyRu(n);
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        const normalized = this.normalizeLines(newValue);
        this.syncNextLineId(normalized);
        this.syncingFromValue = true;
        this.lines = normalized;
      },
    },
    lines: {
      deep: true,
      handler(newValue) {
        if (this.syncingFromValue) {
          this.syncingFromValue = false;
          return;
        }
        this.$emit("input", newValue);
      },
    },
  },
  beforeDestroy() {
    Object.keys(this.productLineSearchTimers).forEach((k) => {
      const timer = this.productLineSearchTimers[k];
      if (timer) clearTimeout(timer);
    });
  },
  methods: {
    makeEmptyLine(lineId) {
      return {
        _lineId: lineId,
        product_id: null,
        quantity: 1,
        unit_price: null,
        productSearch: "",
        /** Имя выбранного товара — чтобы снять выбор только при изменении текста поиска */
        selectedProductName: "",
        product_slug: "",
        product_thumb_url: "",
        productSearchResults: [],
      };
    },
    firstProductPhotoUrl(product) {
      if (
        !product ||
        !Array.isArray(product.photos) ||
        !product.photos.length
      ) {
        return "";
      }
      const ph = product.photos[0];
      return ph.thumbnail_url || ph.featured_url || ph.url || "";
    },
    normalizeLines(lines) {
      const source = Array.isArray(lines) ? lines : [];
      const mapped = source.map((line, idx) => {
        const merged = {
          ...this.makeEmptyLine(line && line._lineId ? line._lineId : idx + 1),
          ...(line || {}),
          productSearchResults: Array.isArray(line && line.productSearchResults)
            ? [...line.productSearchResults]
            : [],
        };

        let productSearch =
          merged.productSearch != null ? String(merged.productSearch) : "";
        let selectedProductName =
          merged.selectedProductName != null
            ? String(merged.selectedProductName)
            : "";

        const pid = merged.product_id;
        const hasPid = pid != null && pid !== "";
        if (
          hasPid &&
          (!selectedProductName || selectedProductName === "") &&
          productSearch
        ) {
          const trimmedPs = productSearch.trim();
          if (trimmedPs === `#${pid}` || trimmedPs === `#${Number(pid)}`) {
            productSearch = "";
            selectedProductName = "";
          } else {
            const esc = String(pid).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const stripRe = new RegExp(`^#${esc}\\s+`);
            if (stripRe.test(productSearch)) {
              productSearch = productSearch.replace(stripRe, "").trim();
              selectedProductName = productSearch;
            }
          }
        }

        return {
          ...merged,
          productSearch,
          selectedProductName,
        };
      });
      return mapped.length ? mapped : [this.makeEmptyLine(1)];
    },
    syncNextLineId(lines) {
      const maxId = lines.reduce((max, line) => {
        const id = Number(line && line._lineId);
        return Number.isFinite(id) ? Math.max(max, id) : max;
      }, 0);
      this.nextProductLineId = Math.max(maxId + 1, 1);
    },
    parseNumberOrEmpty(value) {
      if (value === "" || value == null) return "";
      const n = Number(value);
      return Number.isFinite(n) ? n : "";
    },
    updateLine(idx, patch) {
      const line = this.lines[idx];
      if (!line) return;
      this.$set(this.lines, idx, {
        ...line,
        ...patch,
      });
    },
    addProductLine() {
      const id = this.nextProductLineId++;
      this.lines.push(this.makeEmptyLine(id));
    },
    removeProductLine(idx) {
      if (this.lines.length === 1) return;
      const line = this.lines[idx];
      if (line && line._lineId != null) {
        const timer = this.productLineSearchTimers[line._lineId];
        if (timer) {
          clearTimeout(timer);
          this.$delete(this.productLineSearchTimers, line._lineId);
        }
      }
      this.lines.splice(idx, 1);
    },
    onProductLineSearchInput(idx, value) {
      const line = this.lines[idx];
      if (!line || line._lineId == null) return;

      const patch = { productSearch: value };
      if (line.product_id != null && line.product_id !== "") {
        const s = String(value || "").trim();
        const ref = String(line.selectedProductName || "").trim();
        if (ref) {
          if (s !== ref) {
            patch.product_id = null;
            patch.selectedProductName = "";
            patch.product_slug = "";
            patch.product_thumb_url = "";
          }
        } else if (s !== "") {
          patch.product_id = null;
          patch.selectedProductName = "";
          patch.product_slug = "";
          patch.product_thumb_url = "";
        }
      }
      this.updateLine(idx, patch);

      const lineId = line._lineId;
      const prev = this.productLineSearchTimers[lineId];
      if (prev) clearTimeout(prev);
      this.$set(
        this.productLineSearchTimers,
        lineId,
        setTimeout(() => {
          this.searchProductsForLine(idx);
          this.$delete(this.productLineSearchTimers, lineId);
        }, 300)
      );
    },
    async searchProductsForLine(idx) {
      const line = this.lines[idx];
      if (!line) return;

      let q = (line.productSearch || "").trim();
      q = q.replace(/^#\d+\s+/, "").trim();
      if (!q) {
        this.updateLine(idx, { productSearchResults: [] });
        return;
      }

      try {
        const res = await api.get("/admin/products", {
          params: { query: q, per_page: 20, page: 1 },
        });
        const data = res.data.data || res.data;
        const list = Array.isArray(data) ? data : (data && data.data) || [];
        this.updateLine(idx, { productSearchResults: list });
      } catch (e) {
        this.updateLine(idx, { productSearchResults: [] });
      }
    },
    defaultUnitPriceFromProduct(product) {
      if (!product) return null;
      if (product.is_individual_order) {
        const minPrice = product.price_min;
        if (minPrice != null && minPrice !== "") {
          const n = Number(minPrice);
          if (Number.isFinite(n)) return n;
        }
        return null;
      }

      const currentPrice =
        product.price && product.price.current != null
          ? product.price.current
          : null;
      if (currentPrice != null && currentPrice !== "") {
        const n = Number(currentPrice);
        if (Number.isFinite(n)) return n;
      }
      return null;
    },
    selectProductForLine(idx, product) {
      const line = this.lines[idx];
      if (!line || !product) return;
      const name = (product.name || "").trim();
      const unit = this.defaultUnitPriceFromProduct(product);

      this.updateLine(idx, {
        product_id: product.id,
        selectedProductName: name,
        productSearch: name,
        product_slug: product.slug || "",
        product_thumb_url: this.firstProductPhotoUrl(product),
        productSearchResults: [],
        unit_price: unit != null ? unit : null,
      });
    },
    lineSubtotalAmount(line) {
      const quantity = Number(line.quantity);
      const unitPrice = line.unit_price;
      if (!Number.isFinite(quantity) || quantity <= 0) return null;
      if (unitPrice == null || unitPrice === "") return null;
      const unit = Number(unitPrice);
      if (!Number.isFinite(unit)) return null;
      return quantity * unit;
    },
    lineSubtotalLabel(line) {
      const n = this.lineSubtotalAmount(line);
      if (n == null) return "—";
      return this.formatMoneyRu(n);
    },
    formatMoneyRu(n) {
      return (
        n.toLocaleString("ru-RU", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + " ₽"
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.order-products {
  margin-bottom: 1rem;
  --products-grid-columns: 72px 52px minmax(200px, 1fr) 100px 120px 118px 42px;
  --products-grid-gap: 1rem;

  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    margin: 1.25rem 0 0.75rem;
  }

  &__title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
  }

  &__total {
    margin: 0;
    font-size: 0.95rem;
    color: #374151;
  }

  &__table-head {
    display: grid;
    grid-template-columns: var(--products-grid-columns);
    column-gap: var(--products-grid-gap);
    align-items: end;
    padding: 0 0 0.35rem;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 0.6rem;
  }

  &__head-cell {
    font-size: 0.8125rem;
    line-height: 1.2;
    color: #6b7280;

    &--id {
      text-align: left;
    }

    &--thumb {
      text-align: center;
    }

    &--product {
      min-width: 0;
    }

    &--qty {
      text-align: left;
    }

    &--unit {
      text-align: left;
    }

    &--subtotal {
      text-align: left;
    }

    &--actions {
      width: 42px;
    }
  }

  &__row {
    display: grid;
    grid-template-columns: var(--products-grid-columns);
    column-gap: var(--products-grid-gap);
    align-items: flex-start;
    margin: 0 0 0.5rem;
  }

  &__col {
    box-sizing: border-box;
    margin-bottom: 0;

    &--id,
    &--thumb,
    &--product,
    &--qty,
    &--unit,
    &--subtotal {
      min-width: 0;
    }

    &--thumb {
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding-top: 3px;
    }

    &--product {
      display: flex;
      flex-direction: column;
      gap: 0;
    }

    &--actions {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &__thumb-wrap {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
  }

  &__thumb-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    color: #9ca3af;
    background: #f3f4f6;
    border-radius: 4px;
    border: 1px dashed #d1d5db;
  }

  &__product-row {
    display: flex;
    align-items: flex-start;
    gap: 0.35rem;
    min-width: 0;
    width: 100%;
  }

  &__product-link {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.375rem;
    height: 2.375rem;
    border-radius: 4px;
    color: #2563eb;
    background: rgba(37, 99, 235, 0.08);
    text-decoration: none;
    transition: background 0.15s ease, color 0.15s ease;
    align-self: flex-start;
    margin-top: 3px;

    &:hover:not(.order-products__product-link--inactive) {
      background: rgba(37, 99, 235, 0.2);
      color: #1d4ed8;
    }

    &--inactive {
      opacity: 0.35;
      cursor: default;
      background: #f3f4f6;
      color: #9ca3af;
    }
  }

  &__input {
    width: 100%;
    box-sizing: border-box;
    padding: 0.5rem 0.65rem;
    font-size: 0.9375rem;
    line-height: 1.4;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: #fff;
  }

  &__input--product {
    flex: 1;
    min-width: 0;
  }

  &__product-id {
    box-sizing: border-box;
    margin: 0;
    min-height: 2.375rem;
    padding: 0.5rem 0.35rem;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-size: 0.875rem;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: #4b5563;
    line-height: 1.25;
  }

  &__line-subtotal {
    box-sizing: border-box;
    margin: 0;
    min-height: 2.375rem;
    padding: 0.5rem 0.625rem;
    display: flex;
    align-items: center;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #111827;
    line-height: 1.25;
  }

  &__search-results {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    margin-top: 6px;
    padding: 4px;
    background: #fff;
  }

  &__search-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 8px 10px;
    margin: 0;
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: transparent;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: rgba(37, 99, 235, 0.06);
    }
  }

  &__search-name {
    flex: 1;
    min-width: 0;
    padding-right: 8px;
  }

  &__search-id {
    flex-shrink: 0;
    font-size: 0.8125rem;
    color: #6b7280;
  }

  &__remove-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0.8rem;
    font-size: 0.9375rem;
    line-height: 1.4;
    border-radius: 4px;
    border: 1px solid #dc2626;
    cursor: pointer;
    background: #dc2626;
    color: #fff;

    &:hover:not(:disabled) {
      background: #b91c1c;
      border-color: #b91c1c;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__add-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    margin-bottom: 1rem;
    font-size: 0.9375rem;
    line-height: 1.4;
    border-radius: 4px;
    border: 1px solid #2563eb;
    cursor: pointer;
    background: transparent;
    color: #2563eb;

    &:hover {
      background: rgba(37, 99, 235, 0.08);
    }
  }
}
</style>
